import { create } from 'zustand';

interface CartItem {
  key: string;
  name: string;
  price: number;
  quantity: number;
}

interface CartTransaction {
  transaction_id: string;
  products: { key: string; quantity: number }[];
  payments: { type: 'CB'; amount: number }[];
  phone_number: string | null;
  total: number;
}

interface CartState {
  items: Record<string, CartItem>;
  phoneNumber: string | null;

  addItem: (product: { key: string; name: string; price: number }) => void;
  removeItem: (key: string) => void;
  updateQuantity: (key: string, qty: number) => void;
  setPhoneNumber: (phone: string | null) => void;
  clear: () => void;

  getTotal: () => number;
  getItemCount: () => number;
  buildTransaction: (borneId: string) => CartTransaction;
}

export const useCartStore = create<CartState>()((set, get) => ({
  items: {},
  phoneNumber: null,

  addItem: (product) =>
    set((state) => {
      const existing = state.items[product.key];
      return {
        items: {
          ...state.items,
          [product.key]: existing
            ? { ...existing, quantity: existing.quantity + 1 }
            : { ...product, quantity: 1 },
        },
      };
    }),

  removeItem: (key) =>
    set((state) => {
      const { [key]: _, ...rest } = state.items;
      return { items: rest };
    }),

  updateQuantity: (key, qty) =>
    set((state) => {
      if (qty <= 0) {
        const { [key]: _, ...rest } = state.items;
        return { items: rest };
      }
      const existing = state.items[key];
      if (!existing) return state;
      return {
        items: {
          ...state.items,
          [key]: { ...existing, quantity: qty },
        },
      };
    }),

  setPhoneNumber: (phone) => set({ phoneNumber: phone }),

  clear: () => set({ items: {}, phoneNumber: null }),

  getTotal: () => {
    const { items } = get();
    return Object.values(items).reduce(
      (sum, item) => sum + item.price * item.quantity,
      0,
    );
  },

  getItemCount: () => {
    const { items } = get();
    return Object.values(items).reduce(
      (sum, item) => sum + item.quantity,
      0,
    );
  },

  buildTransaction: (borneId) => {
    const { items, phoneNumber } = get();
    const total = get().getTotal();
    return {
      transaction_id: `${borneId}-${Date.now().toString(36)}`,
      products: Object.values(items).map(({ key, quantity }) => ({
        key,
        quantity,
      })),
      payments: [{ type: 'CB' as const, amount: total }],
      phone_number: phoneNumber,
      total,
    };
  },
}));
